import { motion } from 'motion/react';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import { useState } from 'react';
import { LEGAL } from '../config/legal';

interface FAQItem {
  question: string;
  answer: string | JSX.Element;
  category: string;
}

const faqData: FAQItem[] = [
  // Installation et Configuration
  {
    category: "Installation et Configuration",
    question: "Comment installer MP3 Random Selector ?",
    answer: "Téléchargez le fichier d'installation depuis la section téléchargement de notre site. Exécutez le fichier .msi et suivez les instructions d'installation. L'application sera automatiquement installée dans Program Files et un raccourci sera créé sur votre bureau."
  },
  {
    category: "Installation et Configuration",
    question: "Quels sont les prérequis système ?",
    answer: (
      <div>
        <p className="mb-2">Configuration minimale :</p>
        <ul className="list-disc list-inside space-y-1 text-gray-600 dark:text-gray-400">
          <li>Windows 10 ou version ultérieure</li>
          <li>4 GB de RAM</li>
          <li>50 MB d'espace disque libre</li>
          <li>Résolution d'écran minimale : 1024x768</li>
        </ul>
      </div>
    )
  },
  {
    category: "Installation et Configuration",
    question: "L'application fonctionne-t-elle sur Mac ou Linux ?",
    answer: "Actuellement, MP3 Random Selector est uniquement disponible pour Windows. Nous étudions la possibilité de porter l'application sur Mac et Linux dans les futures versions."
  },

  // Utilisation de base
  {
    category: "Utilisation de base",
    question: "Comment ajouter des dossiers sources ?",
    answer: "Cliquez sur le bouton '+ Ajouter un dossier' dans la section 'Dossiers sources' ou glissez-déposez directement un dossier depuis l'explorateur Windows. Vous pouvez ajouter autant de dossiers que vous le souhaitez, y compris des dossiers réseau."
  },
  {
    category: "Utilisation de base",
    question: "Puis-je utiliser des dossiers réseau ?",
    answer: "Oui, absolument ! L'application supporte les dossiers réseau (ex: \\\\SERVEUR\\DOSSIER). Assurez-vous d'avoir les droits d'accès appropriés au dossier réseau avant de l'ajouter."
  },
  {
    category: "Utilisation de base",
    question: "Comment définir les critères de sélection ?",
    answer: "Utilisez les curseurs dans la section 'Critères' pour définir l'année minimum/maximum, la durée minimum/maximum des pistes, et le nombre de morceaux à sélectionner. Les critères s'appliquent à tous les dossiers sources."
  },
  {
    category: "Utilisation de base",
    question: "Comment prévisualiser la sélection avant de copier ?",
    answer: "Cliquez sur le bouton 'Prévisualiser' après avoir défini vos critères. Une fenêtre s'ouvrira montrant la liste des morceaux qui seront copiés, avec leurs métadonnées (titre, artiste, album, année, durée)."
  },

  // Formats et métadonnées
  {
    category: "Formats et métadonnées",
    question: "Quels formats de fichiers sont supportés ?",
    answer: "Actuellement, l'application supporte uniquement les fichiers MP3. Le support d'autres formats (FLAC, AAC, OGG) est prévu pour les futures versions."
  },
  {
    category: "Formats et métadonnées",
    question: "Comment l'application lit-elle les métadonnées ?",
    answer: "L'application lit les tags ID3 des fichiers MP3 pour extraire le titre, l'artiste, l'album, l'année et la durée. Si certaines métadonnées sont manquantes, le fichier sera tout de même inclus dans la sélection."
  },
  {
    category: "Formats et métadonnées",
    question: "Que se passe-t-il si un fichier MP3 n'a pas de métadonnées ?",
    answer: "Les fichiers sans métadonnées peuvent toujours être sélectionnés et copiés. L'application utilisera le nom du fichier comme titre et appliquera les filtres uniquement sur les métadonnées disponibles."
  },

  // Performance et optimisation
  {
    category: "Performance et optimisation",
    question: "Combien de temps prend le scan des dossiers ?",
    answer: "Le temps de scan dépend du nombre de fichiers et de la vitesse de votre disque. Sur un SSD, la copie peut être rapide, sur un disque réseau, il faut parfois plusieurs minutes pour scanner de grands volumes de données."
  },
  {
    category: "Performance et optimisation",
    question: "L'application garde-t-elle les résultats de scan en mémoire ?",
    answer: "Oui, l'application met en cache les métadonnées scannées pendant la session en cours. Si vous modifiez les dossiers sources, un nouveau scan sera automatiquement effectué."
  },
  {
    category: "Performance et optimisation",
    question: "Puis-je annuler une opération en cours ?",
    answer: "Oui, vous pouvez annuler le scan des dossiers ou la copie des fichiers en cliquant sur le bouton 'Annuler' dans la fenêtre de progression."
  },

  // Problèmes courants
  {
    category: "Problèmes courants",
    question: "L'application ne détecte pas mes fichiers MP3",
    answer: "Vérifiez que les fichiers ont bien l'extension .mp3 et qu'ils ne sont pas corrompus. Assurez-vous également d'avoir les droits de lecture sur le dossier source."
  },
  {
    category: "Problèmes courants",
    question: "La copie échoue vers le dossier de destination",
    answer: "Vérifiez que vous avez les droits d'écriture sur le dossier de destination et qu'il y a suffisamment d'espace disque libre. Le chemin du dossier ne doit pas contenir de caractères spéciaux non supportés."
  },
  {
    category: "Problèmes courants",
    question: "L'application se ferme de manière inattendue",
    answer: "Consultez le fichier de log dans le dossier d'installation pour identifier l'erreur. Essayez de redémarrer l'application en tant qu'administrateur. Si le problème persiste, contactez le support."
  },
  {
    category: "Problèmes courants",
    question: "Les caractères spéciaux dans les noms de fichiers ne s'affichent pas correctement",
    answer: "L'application gère l'encodage UTF-8. Si vous rencontrez des problèmes d'affichage, vérifiez que vos fichiers MP3 utilisent un encodage de métadonnées standard."
  },

  // Paramètres et personnalisation
  {
    category: "Paramètres et personnalisation",
    question: "Mes paramètres sont-ils sauvegardés ?",
    answer: "Oui, tous vos paramètres (dossiers sources, critères de sélection, dossier de destination) sont automatiquement sauvegardés et restaurés au prochain démarrage de l'application."
  },
  {
    category: "Paramètres et personnalisation",
    question: "Puis-je changer le thème de l'interface ?",
    answer: "L'application détecte automatiquement le thème de votre système (clair/sombre). Vous pouvez également basculer manuellement entre les thèmes via le bouton de thème dans l'en-tête."
  },
  {
    category: "Paramètres et personnalisation",
    question: "Comment réinitialiser les paramètres ?",
    answer: "Allez dans les paramètres avancés et cliquez sur 'Réinitialiser les paramètres'. Cela supprimera tous vos dossiers sources et critères sauvegardés."
  },
  {
    category: "Stockage des paramètres",
    question: "Où sont sauvegardés mes paramètres?",
    answer: (
      <div>
        <p className="mb-2">
          Les paramètres sont sauvegardés localement dans un fichier JSON géré par l'application.
        </p>
        <p className="mb-2">Emplacement typique sous Windows :</p>
        <ul className="list-disc list-inside space-y-1 text-gray-600 dark:text-gray-400">
          <li>
            <code>{'C:\\Users\\%USERNAME%\\AppData\\Roaming\\mp3-random-selector\\settings.json'}</code>
          </li>
        </ul>
        <p className="mt-2">
          Vous pouvez copier ce fichier pour effectuer des sauvegardes ou le transférer vers un autre appareil.
        </p>
      </div>
    )
  },
  {
    category: "Stockage des paramètres",
    question: "Où est sauvegardé le cache d'analyse des fichiers MP3 ?",
    answer: (
      <div>
        <p className="mb-2">
          Le cache d'analyse des fichiers MP3 est sauvegardé localement dans un fichier JSON géré par l'application.
        </p>
        <p className="mb-2">Emplacement typique sous Windows :</p>
        <ul className="list-disc list-inside space-y-1 text-gray-600 dark:text-gray-400">
          <li>
            <code>{'C:\\Users\\%USERNAME%\\AppData\\Roaming\\mp3-random-selector\\mp3_cache.json'}</code>
          </li>
        </ul>
        <p className="mt-2">
          Vous pouvez copier ce fichier pour effectuer des sauvegardes ou le transférer vers un autre appareil.
        </p>
      </div>
    )
  },

  {
    category: "Stockage des paramètres",
    question: "Où sont sauvegardées les infos sur la licence ?",
    answer: (
      <div>
        <p className="mb-2">
          Les informations sur la licence sont sauvegardées localement dans un fichier JSON géré par l'application.
        </p>
        <p className="mb-2">Emplacement typique sous Windows :</p>
        <ul className="list-disc list-inside space-y-1 text-gray-600 dark:text-gray-400">
          <li>
            <code>{'C:\\Users\\%USERNAME%\\AppData\\Roaming\\mp3-random-selector\\license.json'}</code>
          </li>
        </ul>
        <p className="mt-2">
          Attention, si vous supprimez ce fichier, l'application ne pourra plus vérifier votre licence et passera en mode limité.
        </p>
      </div>
    ),
  },
  // Sécurité et confidentialité
  {
    category: "Sécurité et confidentialité",
    question: "L'application collecte-t-elle des données personnelles ?",
    answer: "L'application ne lit ni n'envoie aucune information sur vos fichiers MP3 : ils restent sur votre ordinateur. Seule l'activation de la licence contacte nos serveurs. Elle transmet l'adresse e-mail de votre compte, la clé de licence, une empreinte de l'ordinateur (un code calculé à partir de son identifiant matériel, qui ne permet pas de retrouver cet identifiant) et le nom de l'ordinateur. Ces données servent uniquement à contrôler le nombre de postes autorisés par licence. Un journal technique de l'activation (date, résultat, adresse IP) est également conservé pour des raisons de sécurité. Pour exercer vos droits (accès, rectification, suppression), consultez la page Mentions légales."
  },
  {
    category: "Sécurité et confidentialité",
    question: "L'application modifie-t-elle mes fichiers originaux ?",
    answer: "Non, l'application ne fait que copier vos fichiers MP3. Les fichiers sources restent intacts dans leurs dossiers d'origine."
  }
  ,
  {
    category: "Sécurité et confidentialité",
    question: "Pourquoi Windows affiche-t-il un avertissement SmartScreen lors du lancement ?",
    answer: (
      <div>
        <p className="mb-2">
          Windows SmartScreen peut afficher un avertissement pour les exécutables qui ne sont pas signés numériquement ou qui sont peu connus. Cela ne signifie pas forcément que le logiciel est malveillant — SmartScreen évalue la "réputation" du fichier.
        </p>
        <p className="mb-2">Que faire si vous voyez cet avertissement :</p>
        <ul className="list-disc list-inside space-y-1 text-gray-600 dark:text-gray-400">
          <li>Téléchargez toujours l'application depuis notre site officiel : <a className="text-primary-600 underline" href="https://mp3randomselector.fr">mp3randomselector.fr</a>.</li>
          <li>Dans la fenêtre d'avertissement SmartScreen cliquez sur "Informations complémentaires" (ou "More info") puis sur "Exécuter quand même" (ou "Run anyway") pour lancer l'application.</li>
          <li>Si vous voulez une vérification supplémentaire, comparez le hash SHA‑256 du fichier téléchargé avec le hash publié sur notre site ou contactez le support pour le vérifier.</li>
        </ul>
      </div>
    )
  }
  ,
  // Licence
  {
    category: "Licence",
    question: "Comment activer ma licence après l'achat ?",
    answer: (
      <div>
        <p className="mb-2">Après l'achat via notre site, vous recevrez une clé de licence dans votre espace membre.</p>
        <p>Dans l'application, ouvrez le menu 'Licence' et copiez-collez la clé dans le champ d'activation puis cliquez sur 'Activer'.</p>
      </div>
    )
  },
  {
    category: "Licence",
    question: "Quels moyens de paiement sont acceptés ?",
    answer: "Nous acceptons les paiements par carte bancaire via Stripe. Les transactions sont sécurisées et vous recevrez une facture par e‑mail après l'achat."
  },
  {
    category: "Licence",
    question: "Puis-je transférer ma licence vers un autre ordinateur ?",
    answer: "Oui. Vous pouvez désactiver la licence sur l'ordinateur actuel via les paramètres de licence, puis la réactiver sur le nouvel appareil avec la même clé. Si vous rencontrez des problèmes, contactez le support."
  },
  {
    category: "Licence",
    question: "L'application est-elle utilisable sans licence ?",
    answer: "Oui. Sans licence, l'application fonctionne en mode gratuit permanent : vous pouvez utiliser jusqu'à 3 dossiers sources et sélectionner jusqu'à 15 morceaux par session. Pour supprimer ces limites, activez une licence payante."
  },
  {
    category: "Licence",
    question: "Comment obtenir une facture ou une preuve d'achat ?",
    answer: "La facture est automatiquement envoyée à l'adresse e‑mail utilisée lors de l'achat. Si vous ne l'avez pas reçue, contactez le support en indiquant votre numéro de commande."
  },
  {
    category: "Licence",
    question: "Comment demander un remboursement ?",
    answer: (
      <p>
        Vous bénéficiez d'une garantie « satisfait ou remboursé » de {LEGAL.satisfactionGuaranteeDays} jours à
        compter de l'achat. Pour en bénéficier, écrivez à {LEGAL.seller.email} en indiquant l'adresse e-mail de
        votre compte et la référence de votre paiement : le remboursement est effectué sur le moyen de paiement
        utilisé. Les conditions complètes figurent à l'article 5 des{' '}
        <a href="/cgv" className="underline text-purple-300">conditions générales de vente</a>.
      </p>
    )
  }
];

const categories = [...new Set(faqData.map(item => item.category))];

export default function FAQ() {
  const [openItems, setOpenItems] = useState<number[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>("all");

  const toggleItem = (index: number) => {
    setOpenItems(prev =>
      prev.includes(index)
        ? prev.filter(i => i !== index)
        : [...prev, index]
    );
  };

  const filteredFAQ = selectedCategory === "all"
    ? faqData
    : faqData.filter(item => item.category === selectedCategory);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      {/* Header */}
      <section className="relative pt-24 pb-16">
        <div className="absolute inset-0 hero-gradient opacity-50"></div>

        <div className="container mx-auto px-6 relative z-10">
          <motion.div
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-3xl mx-auto"
          >
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              <span className="text-primary-300">Questions Fréquentes</span>
            </h1>
            <p className="text-xl text-gray-700 dark:text-gray-300 leading-relaxed">
              Trouvez rapidement les réponses aux questions les plus courantes sur MP3 Random Selector
            </p>
          </motion.div>
        </div>
      </section>

      {/* FAQ Content */}
      <section className="py-16">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">

            {/* Category Filter */}
            <motion.div
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="mb-8"
            >
              <div className="flex flex-wrap gap-2 justify-center">
                <button
                  onClick={() => setSelectedCategory("all")}
                  className={`px-4 py-2 rounded-full transition-all duration-300 ${selectedCategory === "all"
                    ? 'bg-gradient-to-r from-primary-500 to-accent-500 text-white shadow-lg'
                    : 'bg-white/80 dark:bg-gray-800/80 text-gray-700 dark:text-gray-300 hover:bg-white dark:hover:bg-gray-700'
                    }`}
                >
                  Toutes les catégories
                </button>
                {categories.map((category) => (
                  <button
                    key={category}
                    onClick={() => setSelectedCategory(category)}
                    className={`px-4 py-2 rounded-full transition-all duration-300 ${selectedCategory === category
                      ? 'bg-gradient-to-r from-primary-500 to-accent-500 text-white shadow-lg'
                      : 'bg-white/80 dark:bg-gray-800/80 text-gray-700 dark:text-gray-300 hover:bg-white dark:hover:bg-gray-700'
                      }`}
                  >
                    {category}
                  </button>
                ))}
              </div>
            </motion.div>

            {/* FAQ Items */}
            <motion.div
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="space-y-4"
            >
              {filteredFAQ.map((item, index) => (
                <motion.div
                  key={`${selectedCategory}-${index}`}
                  initial={{ scale: 0.95, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ duration: 0.3, delay: index * 0.05 }}
                  className="card overflow-hidden"
                >
                  <button
                    onClick={() => toggleItem(index)}
                    className="w-full text-left p-6 hover:bg-gray-50/50 dark:hover:bg-gray-700/30 transition-colors duration-200"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="text-sm font-medium text-primary-600 dark:text-primary-400 mb-1">
                          {item.category}
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                          {item.question}
                        </h3>
                      </div>
                      <div className="ml-4 flex-shrink-0">
                        {openItems.includes(index) ? (
                          <ChevronUpIcon className="w-5 h-5 text-gray-500" />
                        ) : (
                          <ChevronDownIcon className="w-5 h-5 text-gray-500" />
                        )}
                      </div>
                    </div>
                  </button>

                  <motion.div
                    initial={false}
                    animate={{
                      height: openItems.includes(index) ? "auto" : 0,
                      opacity: openItems.includes(index) ? 1 : 0
                    }}
                    transition={{ duration: 0.3, ease: "easeInOut" }}
                    className="overflow-hidden"
                  >
                    <div className="px-6 pb-6 text-gray-700 dark:text-gray-300 leading-relaxed">
                      {item.answer}
                    </div>
                  </motion.div>
                </motion.div>
              ))}
            </motion.div>

            {/* Contact Section */}
            <motion.div
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              className="mt-16 text-center"
            >
              <div className="card p-8">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                  Vous ne trouvez pas votre réponse ?
                </h2>
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                  Si vous avez d'autres questions ou rencontrez des problèmes,
                  n'hésitez pas à nous contacter.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <a
                    href="mailto:nouscontacter@mp3randomselector.fr"
                    className="btn-primary"
                  >
                    Contacter le support
                  </a>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
